import * as React from "react";

import { Box, Tooltip, Typography } from "@material-ui/core";

import { OnboardingStatusContext } from "client/context";
import {
  CashPayOnboardingStatusToCurrentStateMap,
  Status,
  StatusLabel,
  StatusMap,
  StatusStep,
  useStyles,
} from "client/domain/OnboardingStatus";
import { useOnboardingStatusContext, useTranslation } from "client/hooks";
import { CashPayOnboardingStatus } from "client/lib/apt-merchant";
import { ZdoService } from "client/services";

import { CashPayIntegration } from "./CashPayIntegration";

const onboardingStatusToCurrentStateMap: CashPayOnboardingStatusToCurrentStateMap = {
  NONE: "None",
  IN_PROGRESS: "None",
  READY_TO_INSTALL: "InProgress",
};

export const determineCurrentState: (
  params: Pick<
    OnboardingStatusContext,
    "cashPayOnboardingStatus" | "cashPayIntegrationComplete"
  >
) => Status = ({ cashPayOnboardingStatus, cashPayIntegrationComplete }) => {
  if (cashPayIntegrationComplete) {
    return "Done";
  }

  if (
    cashPayOnboardingStatus === "READY_TO_INSTALL" &&
    !cashPayIntegrationComplete
  ) {
    return "InProgress";
  }

  return onboardingStatusToCurrentStateMap[
    cashPayOnboardingStatus as CashPayOnboardingStatus
  ];
};

export const CashPayIntegrationStep: React.FC<{
  credentials?: ZdoService.Credentials;
}> = ({ credentials }) => {
  const t = useTranslation();

  const {
    cashPayOnboardingStatus,
    cashPayIntegrationComplete,
    toggleCashPayIntegrationComplete,
  } = useOnboardingStatusContext();

  const classes = useStyles();
  const [currentState, setCurrentState] = React.useState<Status>(
    determineCurrentState({
      cashPayOnboardingStatus,
      cashPayIntegrationComplete,
    })
  );

  React.useEffect(() => {
    setCurrentState(
      determineCurrentState({
        cashPayOnboardingStatus,
        cashPayIntegrationComplete,
      })
    );
  }, [cashPayIntegrationComplete, cashPayOnboardingStatus]);

  const statusMap: StatusMap = {
    Done: {
      className: classes.dark,
    },
    InProgress: {
      className: classes.light,
    },
    None: {},
    Paused: {},
    Reviewing: {
      className: classes.dark,
    },
    RequiresAttention: {},
    Declined: {},
  };
  const { className, text } = statusMap[currentState];

  const isToggleEnabled = currentState === "Done";
  const onClickContainer = () => {
    // allow merchants to see their credentials again if they are in the "Done" state
    if (isToggleEnabled) {
      toggleCashPayIntegrationComplete();
    }
  };

  return (
    <StatusStep
      timeToFade={750}
      activeOnboardingStep="CashPayIntegration"
      stepStatus={currentState}
    >
      <Tooltip title={isToggleEnabled ? "Click to open card" : ""}>
        <Box
          className={`${className} ${classes.statusWrapper} ${
            isToggleEnabled ? classes.cursorPointer : ""
          }`}
          mb={2}
          p={2}
          data-testid="cashpay-integration-step"
        >
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            onClick={onClickContainer}
          >
            <Typography variant="h6">
              <strong>{t("uiAddCashPayToCheckoutShopify")}</strong>
            </Typography>
            <StatusLabel status={currentState} />
          </Box>
          {text && (
            <Box mt={1}>
              <Typography variant="body2">{text}</Typography>
            </Box>
          )}
          {currentState === "InProgress" && (
            <>
              <Box mt={1}>
                <Typography
                  variant="body2"
                  className={classes.cashpayIntegrationInstructions}
                >
                  Please keep these instructions open while linking your Shopify
                  account. This way, you can easily reference your{" "}
                  <strong>Merchant ID</strong> and <strong>Secret Key</strong>{" "}
                  found below.
                </Typography>
              </Box>
              <Box mt={1.5}>
                <CashPayIntegration
                  merchantId={credentials?.merchantId ?? ""}
                  secretKey={credentials?.secretKey ?? ""}
                  handleClickVerifyGatewayEnabled={() =>
                    // TODO: Use KV Store instead to store that cash pay integration is complete instead of LocalStorage
                    toggleCashPayIntegrationComplete()
                  }
                />
              </Box>
            </>
          )}
        </Box>
      </Tooltip>
    </StatusStep>
  );
};
